var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { Input } from '../UI/Input';
import { Search, GraduationCap, BookOpen, Brain, Users, Globe, Star } from 'lucide-react';
var examBoards = [
    {
        id: 'cambridge',
        name: 'Cambridge',
        fullName: 'Cambridge Assessment International Education',
        description: 'IGCSE, AS & A Level qualifications recognized worldwide',
        logo: '/assets/cambridge-logo.png',
        color: {
            primary: '#003366',
            secondary: '#0066CC',
            gradient: 'from-blue-900 to-blue-600'
        },
        stats: {
            subjects: 70,
            students: '5M+',
            countries: 160,
            questions: 15000,
            notes: 2500
        },
        levels: ['IGCSE', 'AS Level', 'A Level'],
        popular: true,
        region: 'International'
    },
    {
        id: 'sat',
        name: 'SAT',
        fullName: 'SAT & SAT Subject Tests',
        description: 'College admission tests for US universities',
        logo: '/assets/sat-logo.png',
        color: {
            primary: '#1E3A8A',
            secondary: '#3B82F6',
            gradient: 'from-blue-800 to-blue-500'
        },
        stats: {
            subjects: 20,
            students: '2M+',
            countries: 180,
            questions: 12000,
            notes: 1800
        },
        levels: ['SAT', 'SAT Subject Tests'],
        popular: true,
        region: 'US'
    },
    {
        id: 'edexcel',
        name: 'Edexcel',
        fullName: 'Pearson Edexcel',
        description: 'GCSE, AS & A Level qualifications by Pearson',
        logo: '/assets/edexcel-logo.png',
        color: {
            primary: '#7C3AED',
            secondary: '#A855F7',
            gradient: 'from-purple-700 to-purple-500'
        },
        stats: {
            subjects: 65,
            students: '3M+',
            countries: 90,
            questions: 11000,
            notes: 2200
        },
        levels: ['GCSE', 'AS Level', 'A Level'],
        popular: true,
        region: 'International'
    },
    {
        id: 'ap',
        name: 'AP',
        fullName: 'Advanced Placement',
        description: 'College-level courses and exams for high school students',
        logo: '/assets/ap-logo.png',
        color: {
            primary: '#DC2626',
            secondary: '#EF4444',
            gradient: 'from-red-700 to-red-500'
        },
        stats: {
            subjects: 38,
            students: '5M+',
            countries: 60,
            questions: 8500,
            notes: 1500
        },
        levels: ['AP'],
        popular: true,
        region: 'US'
    },
    {
        id: 'ib',
        name: 'IB',
        fullName: 'International Baccalaureate',
        description: 'Diploma Programme for international education',
        logo: '/assets/ib-logo.png',
        color: {
            primary: '#7C2D12',
            secondary: '#EA580C',
            gradient: 'from-orange-800 to-orange-600'
        },
        stats: {
            subjects: 50,
            students: '1.5M+',
            countries: 158,
            questions: 7500,
            notes: 1200
        },
        levels: ['DP', 'MYP'],
        popular: false,
        region: 'International'
    },
    {
        id: 'k12',
        name: 'K-12',
        fullName: 'K-12 Curriculum',
        description: 'Standard K-12 education curriculum',
        logo: '/assets/k12-logo.png',
        color: {
            primary: '#059669',
            secondary: '#10B981',
            gradient: 'from-green-700 to-green-500'
        },
        stats: {
            subjects: 15,
            students: '10M+',
            countries: 25,
            questions: 20000,
            notes: 3000
        },
        levels: ['Elementary', 'Middle School', 'High School'],
        popular: false,
        region: 'US'
    }
];
var ExamBoardsHub = function () {
    var _a = useState(''), searchQuery = _a[0], setSearchQuery = _a[1];
    var _b = useState('All'), selectedRegion = _b[0], setSelectedRegion = _b[1];
    var _c = useState(examBoards), filteredBoards = _c[0], setFilteredBoards = _c[1];
    var navigate = useNavigate();
    useEffect(function () {
        var filtered = examBoards;
        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(function (board) {
                return board.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    board.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    board.description.toLowerCase().includes(searchQuery.toLowerCase());
            });
        }
        // Filter by region
        if (selectedRegion !== 'All') {
            filtered = filtered.filter(function (board) { return board.region === selectedRegion; });
        }
        setFilteredBoards(filtered);
    }, [searchQuery, selectedRegion]);
    var handleBoardClick = function (boardId) {
        navigate("/exam-boards/".concat(boardId));
    };
    var popularBoards = filteredBoards.filter(function (board) { return board.popular; });
    var otherBoards = filteredBoards.filter(function (board) { return !board.popular; });
    return (_jsx("div", __assign({ className: "min-h-screen bg-background" }, { children: _jsxs("div", __assign({ className: "max-w-7xl mx-auto px-4 py-8" }, { children: [_jsxs("div", __assign({ className: "text-center mb-12" }, { children: [_jsx("div", __assign({ className: "flex items-center justify-center mb-4" }, { children: _jsx("div", __assign({ className: "w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-4" }, { children: _jsx(GraduationCap, { className: "w-8 h-8 text-white" }) })) })), _jsx("h1", __assign({ className: "text-4xl lg:text-5xl font-bold gradient-text mb-4" }, { children: "Choose Your Exam Board" })), _jsx("p", __assign({ className: "text-lg text-muted-foreground max-w-3xl mx-auto" }, { children: "Access comprehensive study materials, practice questions, and notes tailored to your specific exam board and curriculum." }))] })), _jsxs("div", __assign({ className: "mb-8 space-y-4" }, { children: [_jsxs("div", __assign({ className: "relative max-w-2xl mx-auto" }, { children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" }), _jsx(Input, { placeholder: "Search exam boards, subjects, or qualifications...", value: searchQuery, onChange: function (e) { return setSearchQuery(e.target.value); }, className: "pl-10 h-12 text-lg" })] })), _jsx("div", __assign({ className: "flex justify-center" }, { children: _jsx("div", __assign({ className: "flex flex-wrap gap-2" }, { children: ['All', 'International', 'US', 'UK', 'Regional'].map(function (region) { return (_jsx(Button, __assign({ variant: selectedRegion === region ? 'default' : 'outline', size: "sm", onClick: function () { return setSelectedRegion(region); } }, { children: region }), region)); }) })) }))] })), popularBoards.length > 0 && (_jsxs("section", __assign({ className: "mb-12" }, { children: [_jsxs("div", __assign({ className: "flex items-center mb-6" }, { children: [_jsx(Star, { className: "w-6 h-6 text-warning mr-2" }), _jsx("h2", __assign({ className: "text-3xl font-bold" }, { children: "Popular Exam Boards" }))] })), _jsx("div", __assign({ className: "grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" }, { children: popularBoards.map(function (board) { return (_jsx(ExamBoardCard, { board: board, onClick: function () { return handleBoardClick(board.id); } }, board.id)); }) }))] }))), otherBoards.length > 0 && (_jsxs("section", { children: [_jsx("h2", __assign({ className: "text-3xl font-bold mb-6" }, { children: "All Exam Boards" })), _jsx("div", __assign({ className: "grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" }, { children: otherBoards.map(function (board) { return (_jsx(ExamBoardCard, { board: board, onClick: function () { return handleBoardClick(board.id); } }, board.id)); }) }))] })), filteredBoards.length === 0 && (_jsxs("div", __assign({ className: "text-center py-12" }, { children: [_jsx("div", __assign({ className: "w-24 h-24 bg-muted dark:bg-muted rounded-full flex items-center justify-center mx-auto mb-4" }, { children: _jsx(Search, { className: "w-8 h-8 text-muted-foreground" }) })), _jsx("h3", __assign({ className: "text-xl font-semibold mb-2" }, { children: "No exam boards found" })), _jsx("p", __assign({ className: "text-muted-foreground mb-4" }, { children: "Try adjusting your search or filter criteria" })), _jsx(Button, __assign({ onClick: function () { setSearchQuery(''); setSelectedRegion('All'); } }, { children: "Clear filters" }))] })))] })) })));
};
var ExamBoardCard = function (_a) {
    var board = _a.board, onClick = _a.onClick;
    return (_jsxs(Card, __assign({ className: "group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br ".concat(board.color.gradient, " relative overflow-hidden"), onClick: onClick }, { children: [_jsx("div", { className: "absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300" }), _jsxs(CardContent, __assign({ className: "relative p-6 text-white" }, { children: [_jsxs("div", __assign({ className: "flex items-start justify-between mb-4" }, { children: [_jsxs("div", { children: [_jsx("h3", __assign({ className: "text-xl font-bold mb-1" }, { children: board.name })), _jsx("p", __assign({ className: "text-white/80 text-sm" }, { children: board.fullName }))] }), board.popular && (_jsxs(Badge, __assign({ className: "bg-warning text-warning border-0" }, { children: [_jsx(Star, { className: "w-3 h-3 mr-1" }), "Popular"] })))] })), _jsx("p", __assign({ className: "text-white/90 text-sm mb-4 line-clamp-2" }, { children: board.description })), _jsxs("div", __assign({ className: "flex flex-wrap gap-1 mb-4" }, { children: [board.levels.slice(0, 3).map(function (level) { return (_jsx(Badge, __assign({ variant: "secondary", className: "bg-white/20 text-white border-white/30 text-xs" }, { children: level }), level)); }), board.levels.length > 3 && (_jsxs(Badge, __assign({ variant: "secondary", className: "bg-white/20 text-white border-white/30 text-xs" }, { children: ["+", board.levels.length - 3] })))] })), _jsxs("div", __assign({ className: "grid grid-cols-2 gap-4 mb-4" }, { children: [_jsxs("div", __assign({ className: "text-center" }, { children: [_jsxs("div", __assign({ className: "flex items-center justify-center mb-1" }, { children: [_jsx(BookOpen, { className: "w-4 h-4 mr-1" }), _jsx("span", __assign({ className: "text-lg font-bold" }, { children: board.stats.notes }))] })), _jsx("div", __assign({ className: "text-xs text-white/70" }, { children: "Notes" }))] })), _jsxs("div", __assign({ className: "text-center" }, { children: [_jsxs("div", __assign({ className: "flex items-center justify-center mb-1" }, { children: [_jsx(Brain, { className: "w-4 h-4 mr-1" }), _jsx("span", __assign({ className: "text-lg font-bold" }, { children: board.stats.questions.toLocaleString() }))] })), _jsx("div", __assign({ className: "text-xs text-white/70" }, { children: "Questions" }))] }))] })), _jsxs("div", __assign({ className: "flex items-center justify-between text-xs text-white/70" }, { children: [_jsxs("div", __assign({ className: "flex items-center" }, { children: [_jsx(Users, { className: "w-3 h-3 mr-1" }), board.stats.students, " students"] })), _jsxs("div", __assign({ className: "flex items-center" }, { children: [_jsx(Globe, { className: "w-3 h-3 mr-1" }), board.stats.countries, " countries"] }))] })), _jsx("div", __assign({ className: "absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" }, { children: _jsx("div", __assign({ className: "w-8 h-8 bg-white/20 rounded-full flex items-center justify-center" }, { children: _jsx("span", __assign({ className: "text-white font-bold" }, { children: "\u2192" })) })) }))] }))] })));
};
export default ExamBoardsHub;
